import {
  Box,
  Divider,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import React, { useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import useStyles from '../styles';
import CustomButton from '../../../../components/General/CustomButton';
import ListTile from './ListTile';
import GoogleMaps from '../../../../components/Layout/Google/GoogleMaps';
import { ReduxState } from '../../../../types';
import { filteredDevices } from '../helpers';
import useNodes from '../../../../hooks/useNodes';
import useSensors from '../../../../hooks/useSensors';

const AddPolygons = ({
  title,
  detailedInfoTitle,
  detailedInfo,
  handleCloseModal,
  handleGoBack,
  lat,
  lng,
  insertFunction,
  handleCenterChange,
  handleZoomChange,
  enforcePolygon,
  buttonDisabled,
  isBeingUpdated,
}: {
  title: string,
  detailedInfoTitle: string,
  detailedInfo: {
    [key: string]: string | number,
  },
  handleCloseModal: VoidFunction,
  handleGoBack: VoidFunction,
  lat?: string,
  lng?: string,
  insertFunction: (coordinates?: { lng: number, lat: number }[] | null) => void,
  handleCenterChange?: (centerCoordinates: {
    lat: () => string;
    lng: () => string;
  }) => void,
  handleZoomChange?: (zoom: number) => void,
  enforcePolygon: boolean,
  buttonDisabled: boolean,
  isBeingUpdated?: boolean,
}) => {
  const classes = useStyles();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  const { sensorNodeFilter } = useSelector((state: ReduxState) => state.farmSector);
  const { nodes } = useNodes(selectedFarm?.id ?? '');
  const { sensors } = useSensors(selectedFarm?.id ?? '');
  const allFarmDevices = [...nodes, ...sensors];
  const [coordinates, setCoordinates] = useState<{ lng: number, lat: number }[] | null>([]);
  const [isLoading, setIsLoading] = useState(false);

  const farmCoordinates = selectedFarm?.coordinates
    ? selectedFarm.coordinates.map(({ lat: farmLat, lng: farmLng }) => ({
      lat: typeof farmLat === 'string' ? parseFloat(farmLat) : farmLat,
      lng: typeof farmLng === 'string' ? parseFloat(farmLng) : farmLng,
    })) : null;

  const handleInsert = async (coords?: { lng: number, lat: number }[] | null) => {
    try {
      setIsLoading(true);
      await insertFunction(coords);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box display='flex' flexDirection='column' flex='1' position='relative'>
      {isLoading && (
        <Box
          position='absolute'
          top={0}
          left={0}
          right={0}
          bottom={0}
          display='flex'
          alignItems='center'
          justifyContent='center'
          bgcolor='rgba(255, 255, 255, 0.7)'
          zIndex={1000}
        >
          <CircularProgress />
        </Box>
      )}
      <Box className={classes.uploadKMZModalHeader}>
        <Typography
          variant="h3"
          color="textPrimary"
          className={classes.uploadKMZModalHeaderTitle}
        >
          <span className={classes.activePageUnderlined}>
            {title}
          </span>
        </Typography>
        <IconButton onClick={handleCloseModal} disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider className={classes.divider} />
      <ListTile
        title={detailedInfoTitle}
        tileInformation={detailedInfo}
      />
      <Box border='1px solid black' flex='1'>
        <GoogleMaps
          farm={{
            id: selectedFarm?.id ?? '',
            coordinates: farmCoordinates,
            zoom: selectedFarm?.zoom ?? 18,
          }}
          lat={lat ?? ''}
          lng={lng ?? ''}
          drawable='polygon'
          polygons={selectedFarm?.sectors ?? []}
          setCoordinates={setCoordinates}
          markers={filteredDevices(allFarmDevices, sensorNodeFilter)}
          handleCenterChange={handleCenterChange}
          handleZoomChange={handleZoomChange}
        />
      </Box>
      <Box className={classes.addPolygonButtonContainer}>
        <Box className={classes.singleButtonContainer} marginRight='20px'>
          <CustomButton buttonText='atras' onClick={handleGoBack} disabled={isLoading} />
        </Box>
        {!enforcePolygon && <Box className={classes.singleButtonContainer} marginRight='20px'>
          <CustomButton
            buttonText={isBeingUpdated ? 'Actualizar sin poligono' : 'Agregar sin poligono'}
            onClick={() => handleInsert()}
            color='lightBlue'
            disabled={buttonDisabled || isLoading}
          />
        </Box>}
        <Box className={classes.singleButtonContainer}>
          <CustomButton
            buttonText={isBeingUpdated ? 'Actualizar' : 'Agregar'}
            onClick={() => handleInsert(coordinates)}
            color='lightBlue'
            disabled={buttonDisabled || isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AddPolygons;
